import React, { useCallback, useEffect, useState } from 'react'
import { inject, observer } from 'mobx-react'
import { Button, message, Upload } from 'antd'
import { SendOutlined, UploadOutlined } from '@ant-design/icons'
import { toJS } from 'mobx'

import utils from '../../utils'
import MentionTextInput from '../../components/MentionTextInput/MentionTextInput'
import {
  FormButtonGroup,
  UploadFileListWrapper,
  UploadWrapper,
} from '../../components/CommentComponent/CommentComponentStyled'
import CommentComponent from '../../components/CommentComponent/CommentComponent'
import UploadFileItem from '../../components/Common/FileItemComponent/UploadFileItem'
import { SORT_TYPE } from '../../constants'

const BoardReportComment = props => {
  const { boardReportStore, fileStore, setIsLoadingComment } = props
  const { boardReportCommentList, selectedBoardReport } = boardReportStore

  const reportCode = selectedBoardReport?.code

  const [fileList, setFileList] = useState([])
  const [sort, setSort] = useState(SORT_TYPE.DESCENDING)
  const [commentList, setCommentList] = useState([])
  const [comment, setComment] = useState('')
  const [isShowDelComments, setIsShowDelComments] = useState(false)

  const handleOnchangeComment = (event, newValue) => {
    setComment(newValue)
  }

  const handleChangeSortComment = sort => {
    setSort(sort)
    setCommentList([...commentList.reverse()])
  }

  const handleRemoveFileFromUploadList = fileUID => {
    setFileList(fileList.filter(file => file.uid !== fileUID))
  }

  const handleChangeFile = info => {
    const filtered = info.fileList.filter(
      (elem, idx, arr) => arr.findIndex(f => f.name === elem.name) === idx
    )
    setFileList(filtered)
  }

  const convertList = data => {
    const list = []
    let skipTo = -1
    data.forEach((el, i) => {
      if (!el.deleted) {
        list.push(toJS(el))
      } else if (i > skipTo) {
        const deletedGroup = []
        for (let j = i; j < data.length; j++) {
          skipTo = j
          if (!data[j].deleted) break
          deletedGroup.push(toJS(data[j]))
          if (j === data.length - 1) break
        }
        list.push({
          comment_id: deletedGroup[0].comment_id,
          arrDeletedComment: deletedGroup,
          isHidden: true,
        })
      }
    })
    return list
  }

  const handleCreateComment = async (code, commentData) => {
    try {
      const scrollEl = document.querySelector('.listCommentWrapper')

      await boardReportStore.createBoardReportComment(code, commentData)
      await boardReportStore.getBoardReportComment(code)

      const latest = toJS(boardReportStore.boardReportCommentList).at(-1)
      const newList = [...commentList]

      if (sort === SORT_TYPE.DESCENDING) {
        newList.push(latest)
      } else {
        newList.unshift(latest)
      }

      setCommentList(convertList(newList))
      setComment('')
      scrollEl?.scrollTo(0, scrollEl.scrollHeight)
    } catch (err) {
      console.error(err)
      message.error(err?.vi || 'Gửi ý kiến không thành công')
    }
  }

  const sendComment = useCallback(async () => {
    if (!comment || comment.trim().length === 0) {
      return message.error('Cần nhập ký tự, vui lòng không bỏ trống')
    }

    if (comment.trim().length > 500) {
      return message.error('Ý kiến không vượt quá 500 ký tự')
    }

    const uploadFileList = fileList.map(file => file.originFileObj)
    const batchUploadArr = uploadFileList.map(file => {
      const formData = new FormData()
      formData.append('file', file)
      return fileStore.uploadFile(formData)
    })

    let uploadedFiles = []
    setIsLoadingComment(true)
    try {
      const response = await Promise.all(batchUploadArr)
      uploadedFiles = response.map(res => res.data.file_id)

      await handleCreateComment(reportCode, {
        content: comment.trim(),
        file_ids: uploadedFiles,
      })
      setFileList([])
    } catch (err) {
      console.error(err)
      message.error(err.vi || 'Đã có lỗi xảy ra!')
    } finally {
      setIsLoadingComment(false)
    }
  }, [fileList, commentList, comment])

  const handleDeleteComment = async (comment_id, index) => {
    try {
      setIsLoadingComment(true)
      await boardReportStore.deleteBoardReportComment(reportCode, comment_id)

      const newList = [...commentList]
      newList[index] = { ...newList[index], deleted: true }
      setCommentList(convertList(newList))
      message.success('Xóa ý kiến thành công')
    } catch (err) {
      console.error(err)
      message.error('Xóa ý kiến thất bại')
    } finally {
      setIsLoadingComment(false)
    }
  }

  const getBoardReportComment = async () => {
    try {
      setIsLoadingComment(true)
      const scrollEl = document.querySelector('.listCommentWrapper')
      await boardReportStore.getBoardReportComment(reportCode)
      setCommentList(convertList(boardReportStore.boardReportCommentList))
      scrollEl?.scrollTo(0, scrollEl.scrollHeight)
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoadingComment(false)
    }
  }

  useEffect(() => {
    if (!reportCode) return
    ;(async () => {
      await getBoardReportComment()
    })()
    return () => boardReportStore.clearBoardReportCommentList()
  }, [reportCode])

  const handleShowDeletedComments = async (list, isShow) => {
    if (isShow) {
      const newList = []
      list.forEach(comment => {
        if (comment.isHidden) {
          newList.push(...comment.arrDeletedComment)
          comment.isHidden = false
        } else {
          newList.push(comment)
        }
      })
      setCommentList(newList)
    } else {
      setCommentList(convertList(list))
    }
  }

  const handleToggleShowDelCommentsMenu = () => {
    handleShowDeletedComments(commentList, !isShowDelComments)
    setIsShowDelComments(!isShowDelComments)
  }

  const renderCommentForm = (
    <div className="comment-form">
      <MentionTextInput
        value={comment}
        readonly={false}
        onChange={handleOnchangeComment}
        placeHolder="Viết ý kiến..."
      />
      {fileList.length > 0 && (
        <UploadFileListWrapper>
          {fileList.map(file => (
            <UploadFileItem
              key={file.uid}
              file_id={file.uid}
              file_name={file.name}
              file_type={utils.getExtensionFile(file.name)}
              handleRemoveFileFromUploadList={() =>
                handleRemoveFileFromUploadList(file.uid)
              }
            />
          ))}
        </UploadFileListWrapper>
      )}
      <FormButtonGroup>
        <UploadWrapper>
          <Upload
            fileList={fileList}
            multiple
            onChange={handleChangeFile}
            beforeUpload={() => false}
            showUploadList={false}>
            <Button htmlType="button">
              <UploadOutlined /> Tải file
            </Button>
          </Upload>
        </UploadWrapper>
        <Button onClick={sendComment} type="primary" icon={<SendOutlined />}>
          Đăng ý kiến
        </Button>
      </FormButtonGroup>
    </div>
  )

  return (
    <CommentComponent
      handleToggleShowDelCommentsMenu={handleToggleShowDelCommentsMenu}
      handleDeleteComment={handleDeleteComment}
      isShowDelComments={isShowDelComments}
      handleShowDeletedComment={handleShowDeletedComments}
      commentList={commentList}
      renderCommentForm={renderCommentForm}
      handleChangeSortComment={handleChangeSortComment}
      sortComment={sort}
    />
  )
}

export default inject(
  'boardReportStore',
  'fileStore'
)(observer(BoardReportComment))
